import { memo } from 'react';
import { Helmet } from 'react-helmet';
import type { TOGTags, TOGTagsFAQ } from '../types/util-types';
import { CAPTERRA_REVIEWS } from '../constants/constants';
import UtilsService from './UtilsService';

// FAQ entries whose display answer comes from a raw Trans-style override (e.g.
// '#TEXT# <a href="...">#LINK_TEXT#</a> #TEXT#', meant only for the react-i18next <Trans>
// component to interpolate at render time) can't be resolved to plain text here — using them
// as-is would leak literal placeholder tokens into structured data. Only entries that resolve
// through the plain locale translation (no answer override) are safe to surface in FAQPage
// JSON-LD. Any residual HTML in the translated answer (locales/en/faqs.json occasionally has
// <p>/<1> markup) is stripped, since JSON-LD expects plain text.
export function resolveFaqsForSchema(
    faqs: { id: string; answer?: string }[] | undefined,
    t: (key: string) => string
): TOGTagsFAQ[] {
    if (!faqs?.length) {
        return [];
    }
    return faqs
        .filter(({ answer }) => !answer)
        .map(({ id }) => ({
            question: t(`faqs.${id}.question`),
            answer: t(`faqs.${id}.answer`)
                .replace(/<[^>]*>/g, ' ')
                .replace(/\s+/g, ' ')
                .trim()
        }))
        .filter(({ question, answer }) => Boolean(question) && Boolean(answer));
}

function OGTags({
    description = 'Book clients, manage projects, and get paid all in one place on HoneyBook. The clientflow management platform for independent businesses. Get started with a free 30-day trial.',
    image = 'https://res.cloudinary.com/honeybook/image/upload/f_auto,fl_lossy,q_auto/v1747857032/marketing_site/og_image.jpg',
    url,
    title,
    noIndex,
    lang = 'en',
    faqs
}: TOGTags): React.ReactElement {
    const snippetData = {
        '@context': 'https://schema.org/',
        '@type': 'Product',
        name: title,
        image,
        brand: {
            '@type': 'Brand',
            name: 'HoneyBook'
        },
        aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: CAPTERRA_REVIEWS.rating,
            bestRating: CAPTERRA_REVIEWS.bestRating,
            worstRating: CAPTERRA_REVIEWS.worstRating,
            ratingCount: CAPTERRA_REVIEWS.totalReviews
        }
    };

    // BreadcrumbList derived only from data this component already receives for every page
    // (its real url + title) — no invented category hierarchy, no per-page config needed.
    // Skipped on noindex pages, matching the existing canonical-tag behavior below.
    const breadcrumbData =
        !noIndex && url && title
            ? {
                  '@context': 'https://schema.org/',
                  '@type': 'BreadcrumbList',
                  itemListElement: [
                      {
                          '@type': 'ListItem',
                          position: 1,
                          name: 'Home',
                          item: 'https://www.honeybook.com/'
                      },
                      {
                          '@type': 'ListItem',
                          position: 2,
                          name: title,
                          item: url
                      }
                  ]
              }
            : null;

    // FAQPage only when the caller passed real, already-resolved FAQ content (see
    // resolveFaqsForSchema above) — never rendered on pages with no FAQ data.
    const faqData =
        faqs && faqs.length > 0
            ? {
                  '@context': 'https://schema.org/',
                  '@type': 'FAQPage',
                  mainEntity: faqs.map(({ question, answer }) => ({
                      '@type': 'Question',
                      name: question,
                      acceptedAnswer: {
                          '@type': 'Answer',
                          text: answer
                      }
                  }))
              }
            : null;

    return (
        <Helmet htmlAttributes={{ lang }}>
            <title>{title} | HoneyBook</title>
            <meta name="description" content={description} />
            <meta property="og:url" content={url} />
            <meta property="og:title" content={title} />
            <meta property="og:description" content={description} />
            <meta property="og:image" content={image} />
            <meta property="fb:app_id" content={process.env.GATSBY_fbAppId} />

            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:site" content={url} />
            <meta name="twitter:creator" content="@honeybook" />
            <meta name="twitter:title" content={title} />
            <meta name="twitter:description" content={description} />
            <meta name="twitter:image" content={image} />
            <script type="application/ld+json">
                {JSON.stringify(snippetData)}
            </script>
            {breadcrumbData && (
                <script type="application/ld+json">
                    {JSON.stringify(breadcrumbData)}
                </script>
            )}
            {faqData && (
                <script type="application/ld+json">
                    {JSON.stringify(faqData)}
                </script>
            )}
            {(noIndex || !UtilsService.isProduction()) && (
                <meta name="robots" content="noindex, follow" />
            )}
            {!noIndex && <link rel="canonical" href={url} />}
        </Helmet>
    );
}

export default memo(OGTags);
